// components/DashboardPreview.tsx
import React, { useState, useEffect } from 'react';
import { CodeBlock } from '../types/chat';

interface DashboardPreviewProps {
  codeBlocks: CodeBlock[];
  currentMessageId?: string;
  initialHtml?: string;  // Pre-rendered HTML from backend — skips re-execution
  sessionId?: string;
}

interface DashboardResult {
  success: boolean;
  has_dashboard: boolean;
  html_dashboard?: string;
  pdf_data?: string;  // Base64 encoded PDF
  pdf_filename?: string;
  execution_output?: string;
  execution_time?: string;
  memory_used?: string;
  error?: string;
}

const DashboardPreview: React.FC<DashboardPreviewProps> = ({ codeBlocks, currentMessageId, initialHtml, sessionId }) => {
  const [dashboardData, setDashboardData] = useState<DashboardResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find code blocks that might be dashboards (Python or HTML)
  const pythonBlocks = codeBlocks.filter(block =>
    ['python', 'py'].includes(block.language.toLowerCase())
  );

  // Find HTML code blocks (direct HTML dashboards)
  const htmlBlocks = codeBlocks.filter(block =>
    block.language.toLowerCase() === 'html'
  );

  // Combine both for dashboard detection
  const dashboardBlocks = [...pythonBlocks, ...htmlBlocks];

  // Simple hash function for code content
  const hashCode = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  };

  // sessionStorage helpers for cross-reopen caching
  const getCacheKey = (codeHash: string): string =>
    `dashboard_cache_${currentMessageId || 'unknown'}_${codeHash}`;

  const getCachedHtml = (codeHash: string): string | null => {
    try {
      return sessionStorage.getItem(getCacheKey(codeHash));
    } catch { return null; }
  };

  const setCachedHtml = (codeHash: string, html: string) => {
    try {
      sessionStorage.setItem(getCacheKey(codeHash), html);
    } catch { /* sessionStorage full or unavailable — ignore */ }
  };

  const generateDashboard = async (codeBlock: CodeBlock) => {
    const codeHash = hashCode(codeBlock.code);

    // Check sessionStorage first (survives workspace close/reopen)
    const cached = getCachedHtml(codeHash);
    if (cached) {
      console.log('✅ Using cached dashboard result from sessionStorage, skipping API call');
      setDashboardData({
        success: true,
        has_dashboard: true,
        html_dashboard: cached,
        execution_time: 'cached',
        memory_used: 'N/A'
      });
      return;
    }

    // Check in-memory state (same mount)
    if (dashboardData?.html_dashboard && hashCode(codeBlock.code) === codeHash) {
      console.log('✅ Using in-memory cached dashboard result');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${API_BASE_URL}/execute-dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          code: codeBlock.code,
          language: codeBlock.language,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Debug: Log response content type
      console.log('Response Content-Type:', response.headers.get('content-type'));

      const responseText = await response.text();
      console.log('Raw response:', responseText.substring(0, 200) + '...');

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (jsonError) {
        console.error('JSON parsing failed. Response was:', responseText);
        throw new Error('Server returned invalid JSON response. Check backend logs.');
      }

      if (result.success) {
        setDashboardData(result);
        // Persist HTML to sessionStorage for cross-reopen cache
        if (result.html_dashboard) {
          setCachedHtml(codeHash, result.html_dashboard);
        }
        console.log('✅ Dashboard cached in sessionStorage for future use');
      } else {
        setError(result.error || 'Dashboard generation failed');
      }

    } catch (err: any) {
      console.error('Dashboard generation error:', err);

      // Better error messages
      let errorMessage = 'Failed to generate dashboard';
      if (err.message?.includes('JSON')) {
        errorMessage = 'Server returned invalid response. Check if backend is running properly.';
      } else if (err.message?.includes('HTTP')) {
        errorMessage = `Server error: ${err.message}`;
      } else if (err.message?.includes('fetch')) {
        errorMessage = 'Cannot connect to backend. Please check if the server is running.';
      } else {
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadDashboardHtml = () => {
    if (dashboardData?.html_dashboard) {
      const blob = new Blob([dashboardData.html_dashboard], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dashboard.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const openExpandedDashboard = () => {
    if (dashboardData?.html_dashboard) {
      // Inject a fixed download button into the HTML for the new window
      const downloadBtn = `<div style="position:fixed;top:12px;right:12px;z-index:99999;">
        <button onclick="(function(){var b=new Blob([document.documentElement.outerHTML],{type:'text/html'}),u=URL.createObjectURL(b),a=document.createElement('a');a.href=u;a.download='dashboard.html';document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(u);this.parentElement.remove();})()" style="padding:6px 14px;background:#7c3aed;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:system-ui;box-shadow:0 2px 8px rgba(0,0,0,0.2);">Download HTML</button>
      </div>`;
      const htmlWithBtn = dashboardData.html_dashboard.replace(
        /(<body[^>]*>)/i,
        `$1${downloadBtn}`
      );
      // Fallback: if no <body> tag, prepend
      const finalHtml = htmlWithBtn.includes(downloadBtn) ? htmlWithBtn : downloadBtn + dashboardData.html_dashboard;

      const blob = new Blob([finalHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, '_blank',
        'width=1200,height=800,scrollbars=yes,resizable=yes'
      );

      // Cleanup
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      if (newWindow) {
        newWindow.focus();
      }
    }
  };

  // Auto-generate dashboard if code contains dashboard-related content
  useEffect(() => {
    // PRIORITY 1: Use pre-rendered HTML from backend (no execution needed)
    if (initialHtml) {
      if (dashboardData?.html_dashboard === initialHtml) return; // already showing
      console.log('[DASHBOARD PREVIEW] Using pre-rendered HTML from backend, skipping execution');
      setDashboardData({
        success: true,
        has_dashboard: true,
        html_dashboard: initialHtml,
        execution_time: 'cached',
        memory_used: 'N/A'
      });
      // Also cache in sessionStorage for re-opens
      const codeHash = hashCode(initialHtml);
      setCachedHtml(codeHash, initialHtml);
      return;
    }

    // PRIORITY 2: Check for direct HTML blocks
    if (htmlBlocks.length > 0) {
      const htmlBlock = htmlBlocks[0]; // Use first HTML block
      const codeHash = hashCode(htmlBlock.code);

      // Skip if already displaying this exact HTML
      if (dashboardData?.html_dashboard === htmlBlock.code) return;

      console.log('[DASHBOARD PREVIEW] Direct HTML detected, displaying without execution');
      setDashboardData({
        success: true,
        has_dashboard: true,
        html_dashboard: htmlBlock.code,
        execution_time: '0',
        memory_used: 'N/A'
      });
      // Also cache in sessionStorage for re-opens
      setCachedHtml(codeHash, htmlBlock.code);
    } else if (pythonBlocks.length > 0) {
      // Python code - need to execute to get HTML
      const dashboardBlock = pythonBlocks.find(block => {
        const code = block.code.toLowerCase();
        return code.includes('html') ||
               code.includes('dashboard') ||
               code.includes('chart') ||
               code.includes('plot') ||
               code.includes('matplotlib') ||
               code.includes('plotly') ||
               code.includes('bokeh') ||
               code.includes('kpi') ||
               (code.includes('print(') && (code.includes('<') || code.includes('<!doctype'))) ||
               code.includes('sales') ||
               code.includes('<!DOCTYPE') ||
               code.includes('<html>') ||
               code.includes('<div') ||
               code.includes('<style') ||
               code.includes('pdf') ||
               code.includes('reportlab') ||
               code.includes('pypdf');
      });

      if (dashboardBlock) {
        console.log('[DASHBOARD PREVIEW] Python dashboard code detected, executing...');
        generateDashboard(dashboardBlock);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeBlocks, initialHtml]);

  if (dashboardBlocks.length === 0 && !initialHtml) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="space-y-4 max-w-md">
          <h4 className="text-lg font-semibold text-foreground">📊 Dashboard & PDF Preview</h4>
          <p className="text-sm text-muted-foreground">
            Run Python code that generates HTML dashboards or PDF documents to see live previews here.
          </p>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-foreground">Examples:</p>
            <ul className="text-xs text-muted-foreground space-y-1 text-left">
              <li>• Matplotlib charts with HTML output</li>
              <li>• Plotly interactive dashboards</li>
              <li>• PDF documents with ReportLab</li>
              <li>• Custom HTML with CSS styling</li>
              <li>• Data visualization tables</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Auto-generate on load - no manual buttons */}
      {dashboardData?.has_dashboard && (
        <div className="flex justify-end gap-2">
          <button
            onClick={downloadDashboardHtml}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg text-sm font-medium"
            title="Download as HTML file"
          >
            Download HTML
          </button>
          <button
            onClick={openExpandedDashboard}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all shadow-md hover:shadow-lg text-sm font-medium"
            title="Open in new window"
          >
            Open in New Window
          </button>
        </div>
      )}

      <div className="space-y-4">
        {loading && (
          <div className="flex flex-col items-center justify-center p-8 space-y-3">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Generating Dashboard...</p>
          </div>
        )}

        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg space-y-3">
            <h4 className="text-lg font-semibold text-destructive">❌ Dashboard Generation Failed</h4>
            <pre className="text-xs text-destructive bg-destructive/5 p-3 rounded overflow-x-auto">{error}</pre>
            <button onClick={() => setError(null)} className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
              Try Again
            </button>
          </div>
        )}

        {dashboardData && !loading && (
          <div className="space-y-4">
            {dashboardData.has_dashboard || dashboardData.pdf_data ? (
              <div className="space-y-3">
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <span>⏱️</span>
                    <span>Generated in {dashboardData.execution_time}s</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <span>💾</span>
                    <span>{dashboardData.memory_used}KB</span>
                  </span>
                </div>

                {/* PDF Viewer */}
                {dashboardData.pdf_data && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-foreground">📄 PDF Generated</h4>
                      <a
                        href={`data:application/pdf;base64,${dashboardData.pdf_data}`}
                        download={dashboardData.pdf_filename || 'generated.pdf'}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                      >
                        ⬇️ Download PDF
                      </a>
                    </div>
                    <div className="border border-border rounded-lg overflow-hidden bg-background">
                      <object
                        data={`data:application/pdf;base64,${dashboardData.pdf_data}`}
                        type="application/pdf"
                        className="w-full h-96"
                      >
                        <div className="p-8 text-center space-y-4">
                          <p className="text-muted-foreground">Your browser doesn't support inline PDF viewing.</p>
                          <a
                            href={`data:application/pdf;base64,${dashboardData.pdf_data}`}
                            download={dashboardData.pdf_filename || 'generated.pdf'}
                            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                          >
                            Download PDF Instead
                          </a>
                        </div>
                      </object>
                    </div>
                  </div>
                )}

                {/* HTML Dashboard Viewer */}
                {dashboardData.html_dashboard && !dashboardData.pdf_data && (
                  <div className="border border-border rounded-lg overflow-hidden bg-background">
                    <iframe
                      srcDoc={dashboardData.html_dashboard}
                      className="w-full border-0"
                      style={{ height: 'calc(100vh - 300px)', minHeight: '600px' }}
                      title="Generated Dashboard"
                      sandbox="allow-scripts"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <h4 className="text-lg font-semibold text-foreground">📄 Code Executed Successfully</h4>
                <p className="text-sm text-muted-foreground">The Python code ran but didn't generate HTML dashboard output.</p>
                {dashboardData.execution_output && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-foreground">Output:</h5>
                    <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">{dashboardData.execution_output}</pre>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  💡 Tip: Include HTML output in your Python code using <code className="bg-background px-1 rounded">print(html_content)</code>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPreview;